import {
  catchErrorInto,
  effect,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  OnEvent,
  onStart,
  Service,
  smartRetry,
} from '@toeverything/infra';
import { switchMap, tap, timer } from 'rxjs';

import { type AuthService } from '../../cloud';
import { ApplicationFocused } from '../../lifecycle';
import type { NotificationStore } from '../stores/notification';

@OnEvent(ApplicationFocused, s => s.handleApplicationFocused)
export class NotificationCountService extends Service {
  constructor(
    private readonly store: NotificationStore,
    private readonly authService: AuthService
  ) {
    super();
  }

  loggedIn$ = this.authService.session.status$.map(v => v === 'authenticated');

  readonly count$ = LiveData.from(this.store.watchNotificationCountCache(), 0);
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);

  /**
   * sl-djze.2: Badge count = GraphQL notification count (includes PendingDecision notifications).
   * No longer adds separate pendingDecisionService.count$ — PendingDecisions are regular notifications now.
   */
  readonly totalCount$ = this.count$;

  revalidate = effect(
    switchMap(() => timer(0)),
    exhaustMapWithTrailing(() => {
      return fromPromise(signal => {
        if (!this.loggedIn$.value) {
          return Promise.resolve(0);
        }
        return this.store.getNotificationCount(signal);
      }).pipe(
        tap(result => {
          this.setCount(result ?? 0);
        }),
        smartRetry(),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isLoading$.setValue(true);
        }),
        onComplete(() => this.isLoading$.setValue(false))
      );
    })
  );

  handleApplicationFocused() {
    this.revalidate();
  }

  setCount(count: number) {
    this.store.setNotificationCountCache(count);
  }

  override dispose(): void {
    super.dispose();
    this.revalidate.unsubscribe();
  }
}
